import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from 'src/environments/environment';
import {
  ClientKey,
  Client,
  Referrer,
  NewClientKeyRequest
} from '../../shared/interfaces/client-key.interface';
import { HealthCheckResponse } from '../../shared/interfaces/health-check.interface';
import {
  PixelErrorsReport,
  PixelVolumeReport,
  ReferrerPathBreakdownReport,
  TimeframeValue,
  UserAgentInsightsReports
} from '@pxl/shared/interfaces/reports.interface';

@Injectable()
export class ApiService {
  constructor(private http: HttpClient) {}

  getClients(): Observable<Client[]> {
    return this.http.get<Client[]>(`${environment.backendUrl}admin/clients`);
  }

  getClientKeys(clientId: number): Observable<ClientKey[]> {
    return this.http.get<ClientKey[]>(
      `${environment.backendUrl}admin/client-keys?client_id=${clientId}`
    );
  }

  addClient(newClient: string): Observable<Client> {
    return this.http.post<Client>(`${environment.backendUrl}admin/clients`, { name: newClient });
  }

  updateClient(clientId: number, newClient: string): Observable<Client> {
    return this.http.put<Client>(`${environment.backendUrl}admin/clients/${clientId}`, {
      name: newClient
    });
  }

  addClientKey(request: NewClientKeyRequest): Observable<ClientKey> {
    return this.http.post<ClientKey>(`${environment.backendUrl}admin/client-keys`, request);
  }

  getReferrers(clientKeyId: number): Observable<Referrer[]> {
    return this.http.get<Referrer[]>(
      `${environment.backendUrl}admin/client-key-referrers?client_key=${clientKeyId}`
    );
  }

  addReferrer(clientKeyId: number, referrer: string): Observable<Referrer> {
    return this.http.post<Referrer>(`${environment.backendUrl}admin/client-key-referrers`, {
      client_key_id: clientKeyId,
      referrer
    });
  }

  deleteClientKey(clientKeyId: number): Observable<void> {
    return this.http.delete<void>(`${environment.backendUrl}admin/client-keys/${clientKeyId}`);
  }

  deleteReferrer(referrerId: number): Observable<void> {
    return this.http.delete<void>(
      `${environment.backendUrl}admin/client-key-referrers/${referrerId}`
    );
  }

  getHealthCheck(): Observable<HealthCheckResponse> {
    return this.http.get<HealthCheckResponse>(`${environment.backendUrl}healthcheck`);
  }

  generatePixelVolumeReport(timeframe: TimeframeValue): Observable<PixelVolumeReport> {
    return this.http.get<PixelVolumeReport>(`${environment.backendUrl}reports/pixel-volume`, {
      params: { timeframe }
    });
  }

  generateUserAgentInsightsReport(params: {
    timeframe?: string;
    client_id?: number;
    start_date?: string;
    end_date?: string;
  }): Observable<UserAgentInsightsReports> {
    const filteredParams = Object.fromEntries(
      Object.entries({ ...params }).filter(([, value]) => !!value)
    );

    return this.http.get<UserAgentInsightsReports>(
      `${environment.backendUrl}reports/user-agent-insights`,
      {
        params: filteredParams
      }
    );
  }

  generatePixelErrorsReport(timeframe: TimeframeValue): Observable<PixelErrorsReport> {
    return this.http.get<PixelErrorsReport>(`${environment.backendUrl}reports/pixel-errors`, {
      params: { timeframe }
    });
  }

  generateReferrerPathBreakdownReport(
    clientId: number,
    params: { timeframe?: TimeframeValue; start_date?: string; end_date?: string }
  ) {
    const filteredParams = Object.fromEntries(
      Object.entries({ client_id: clientId, ...params }).filter(([, value]) => !!value)
    );
    return this.http.get<ReferrerPathBreakdownReport>(
      `${environment.backendUrl}reports/referrer-path-breakdown`,
      { params: filteredParams }
    );
  }
}
